import { useEffect, useRef, useState } from "react";

/**
 * The code in a pasted text. A run of exactly `length` digits standing on its
 * own is the code ("Your code is 663970", whatever other numbers surround
 * it); failing that, the digits of one spaced or hyphenated group that add up
 * to `length` ("On 31 Dec your code is 663 970"); failing that, every digit in
 * order. At most `length` digits; empty when there are none.
 */
export function codeFromText(text: string, length = 6): string {
  const alone = new RegExp(`(?:^|\\D)(\\d{${length}})(?!\\d)`).exec(text);
  if (alone?.[1]) return alone[1];
  for (const [group] of text.matchAll(/\d+(?:[ \u00a0-]\d+)*/g)) {
    const digits = group.replace(/\D/g, "");
    if (digits.length === length) return digits;
  }
  return text.replace(/\D/g, "").slice(0, length);
}

/**
 * The host's "Paste code" action: reads the clipboard on a tap and hands over
 * the code in it. Null where the browser cannot read the clipboard, so the
 * host shows nothing; null on the server and the first render too, so the
 * markup hydrates. A clipboard the visitor would not share, or one holding
 * no digits, hands over an empty string: the host puts the caret back.
 */
export function usePasteCode(
  onCode: (code: string) => void,
  length = 6,
): (() => void) | null {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    setAvailable(typeof navigator.clipboard?.readText === "function");
  }, []);
  // The read waits on the visitor (a permission prompt, the system Paste
  // bubble) while the host re-renders. Calling the captured `onCode` would
  // hand the code to a render that no longer knows a code is being checked,
  // so keep the current one.
  const onCodeRef = useRef(onCode);
  useEffect(() => {
    onCodeRef.current = onCode;
  }, [onCode]);
  // A read that lands after the step has gone hands over nothing.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  if (!available) return null;
  return () => {
    navigator.clipboard
      .readText()
      .then((text) => codeFromText(text, length))
      .catch(() => "")
      .then((code) => {
        if (mounted.current) onCodeRef.current(code);
      })
      .catch(() => undefined);
  };
}
